import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Typography, TypographyProps } from '@mui/material';

import { AppRoutes } from '@constants';

const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

const TextLink: React.FC<{
  href: string;
  children: React.ReactNode;
  newTab?: boolean;
  // Grey, in the colour of the text around it: for the small print on sign-in and in the menu.
  muted?: boolean;
}> = ({ href, children, newTab, muted }) => {
  const tabProps = newTab ? { target: '_blank', rel: 'noopener' } : {};

  return href.startsWith('/') ? (
    <Link
      component={RouterLink}
      to={href}
      color={muted ? 'inherit' : 'text.link'}
      {...tabProps}
    >
      {children}
    </Link>
  ) : (
    <Link
      href={href}
      color="text.link"
      {...(href.startsWith('mailto:')
        ? {}
        : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </Link>
  );
};

// Renders a paragraph whose links are written as [text](href), as in `constants/legal.ts`.
export const LinkedText: React.FC<{ text: string } & TypographyProps> = ({
  text,
  ...typographyProps
}) => {
  const parts: React.ReactNode[] = [];
  const pattern = new RegExp(MARKDOWN_LINK);
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    const [whole, label, href] = match;
    const start = match.index;
    parts.push(text.slice(last, start));
    parts.push(
      <TextLink key={start} href={href}>
        {label}
      </TextLink>,
    );
    last = start + whole.length;
  }
  parts.push(text.slice(last));

  return <Typography {...typographyProps}>{parts}</Typography>;
};

const TERMS_PATH = `/${AppRoutes.TERMS}`;
const PRIVACY_PATH = `/${AppRoutes.PRIVACY}`;

// The two documents open in a new tab, so a half-filled form or an open page is not lost.
export const LegalLinks: React.FC = () => (
  <Typography variant="caption" color="text.secondary">
    <TextLink href={TERMS_PATH} newTab muted>
      Terms of Service
    </TextLink>
    {' · '}
    <TextLink href={PRIVACY_PATH} newTab muted>
      Privacy Policy
    </TextLink>
  </Typography>
);

// The agreement line under a sign-in or set-password form, as in Figma (client > login).
export const LegalConsentNote: React.FC<{ lead: string }> = ({ lead }) => (
  <Typography variant="body2" color="text.secondary">
    {lead}{' '}
    <TextLink href={TERMS_PATH} newTab muted>
      terms of service
    </TextLink>{' '}
    and{' '}
    <TextLink href={PRIVACY_PATH} newTab muted>
      privacy policy
    </TextLink>
    .
  </Typography>
);
